import { useEffect, useState } from "react";
import { isImpersonating } from "@/features/admin/impersonation/lib/impersonationSession";
import { getSessionToken } from "@/features/bucket/api";
import { getQuota, RecognizeError } from "../api/recognize";
import type { Quota } from "../types";

/**
 * Кому видна карточка «Что в блюде» (владелец 03.11.2026): решает сервер. GET /api/food/quota на странице диеты:
 * 200 — карточка есть (eligible — настоящее распознавание, eligible: false — пример без ИИ); 401/403 (раздел
 * закрыт этому человеку: FOOD_AUDIENCE на сервере, вход «под пользователем») — карточки нет. Сбой (сеть, 5xx) —
 * раздел не закрыт: спрашиваем снова через RETRY_MS, а потом — когда страница снова на экране или сеть вернулась;
 * пока не ответил — карточки нет («pending»). Так же и 503 diagnostic_unavailable (раздел открыт всем, а запрет
 * записи диагностики не подтверждён): это не «своей диеты нет» — ни примера, ни заказа диагностики, ждём ответа,
 * но спрашиваем реже — не раньше 30 с и дальше всё реже (unknownRetryWait), как сайт .life.
 * «Рецепты», «Питание» и дневник от этого не открываются — у них свой переключатель (RECIPES_AUDIENCE).
 *
 * Вход «под пользователем» распознавания не тратит и не покупает: там не спрашиваем вовсе; без сеанса — тоже.
 * Счётчик сменился (оплата пакета — FoodPackPurchaseRoot) — announceFoodQuota: карточки на странице берут новый.
 */

export type FoodAccessState = "pending" | "open" | "closed";

export interface FoodAccess {
    state: FoodAccessState;
    quota: Quota | null;
}

export const FOOD_QUOTA_EVENT = "food-quota-changed";

/** Новый счётчик (или null — «спросите сервер заново») для карточек «Что в блюде» на странице. */
export const announceFoodQuota = (quota: Quota | null = null): void => {
    try {
        window.dispatchEvent(new CustomEvent<Quota | null>(FOOD_QUOTA_EVENT, { detail: quota }));
    } catch {
        /* без CustomEvent — карточка спросит при следующем открытии страницы */
    }
};

const CLOSED: FoodAccess = { state: "closed", quota: null };
const PENDING: FoodAccess = { state: "pending", quota: null };

/** Сбой сервера или сети — повторы через столько мс. */
export const RETRY_MS = [2_000, 8_000, 30_000];

/**
 * 503 diagnostic_unavailable: спросить снова не раньше этого… Сервер помнит неудачную проверку замка диагностики
 * 30 с (food_recognize.DIAG_LOCK_TTL_FAIL_S) — раньше ответ тот же. Его Retry-After сайт не читает: сервер сайта —
 * другой источник (BACKEND_API_URL), а Access-Control-Expose-Headers у него нет.
 */
export const UNKNOWN_RETRY_MIN_MS = 30_000;
/** …с удвоением, но не дольше этого между вопросами… */
export const UNKNOWN_RETRY_MAX_MS = 5 * 60_000;
/** …и не больше стольких раз подряд; дальше — когда страница снова на экране или вернулась сеть. */
export const UNKNOWN_RETRIES = 6;

/** Через сколько спросить снова после tries-го подряд 503 diagnostic_unavailable: 30, 60, 120, 240, 300, 300 с. */
export const unknownRetryWait = (tries: number): number => Math.min(UNKNOWN_RETRY_MAX_MS, UNKNOWN_RETRY_MIN_MS * 2 ** tries);

/** 503 diagnostic_unavailable: сервер сейчас не решает, готова ли своя диета (а не сбой и не «раздел закрыт»). */
export const isDiagUnavailable = (err: unknown): boolean =>
    err instanceof RecognizeError && err.kind === "unavailable" && err.code === "diagnostic_unavailable";

/** Ответ сервера «раздел не для вас» (а не сбой): 401/403. */
const closedBy = (err: unknown) => err instanceof RecognizeError && (err.kind === "auth" || err.kind === "no_diagnostic");

export const useFoodAccess = (enabled: boolean): FoodAccess => {
    const on = enabled && !isImpersonating() && !!getSessionToken();
    const [access, setAccess] = useState<FoodAccess>(on ? PENDING : CLOSED);
    const [attempt, setAttempt] = useState(0);
    // все повторы после сбоя не удались — спросим, когда страница снова на экране или вернулась сеть
    const [gaveUp, setGaveUp] = useState(false);

    useEffect(() => {
        if (!on) {
            setAccess(CLOSED);
            return undefined;
        }
        const ctrl = new AbortController();
        let timer: ReturnType<typeof setTimeout> | undefined;
        // повторы после сбоя (RETRY_MS) и после 503 diagnostic_unavailable (unknownRetryWait) — каждый своим счётом
        let tries = 0;
        let diagTries = 0;
        setGaveUp(false);
        const probe = () => {
            getQuota(ctrl.signal)
                .then((quota) => {
                    if (!ctrl.signal.aborted) setAccess({ state: "open", quota });
                })
                .catch((err: unknown) => {
                    if (ctrl.signal.aborted) return;
                    if (closedBy(err)) {
                        setAccess(CLOSED);
                        return;
                    }
                    // сбой — раздел не закрыт: уже открыта — остаётся как была, нет — ждёт повтора
                    setAccess((cur) => (cur.state === "open" ? cur : PENDING));
                    // 503 diagnostic_unavailable: раньше 30 с ответ тот же — не раньше, и дальше всё реже
                    if (isDiagUnavailable(err)) {
                        if (diagTries < UNKNOWN_RETRIES) timer = setTimeout(probe, unknownRetryWait(diagTries++));
                        else setGaveUp(true);
                        return;
                    }
                    if (tries < RETRY_MS.length) timer = setTimeout(probe, RETRY_MS[tries++]);
                    else setGaveUp(true);
                });
        };
        probe();
        return () => {
            ctrl.abort();
            clearTimeout(timer);
        };
    }, [on, attempt]);

    useEffect(() => {
        if (!on || !gaveUp) return undefined;
        const again = () => {
            if (document.visibilityState !== "hidden") setAttempt((n) => n + 1);
        };
        window.addEventListener("online", again);
        document.addEventListener("visibilitychange", again);
        return () => {
            window.removeEventListener("online", again);
            document.removeEventListener("visibilitychange", again);
        };
    }, [on, gaveUp]);

    useEffect(() => {
        if (!on) return undefined;
        const onQuota = (e: Event) => {
            const quota = (e as CustomEvent<Quota | null>).detail;
            if (quota) setAccess({ state: "open", quota });
            else setAttempt((n) => n + 1);
        };
        window.addEventListener(FOOD_QUOTA_EVENT, onQuota);
        return () => window.removeEventListener(FOOD_QUOTA_EVENT, onQuota);
    }, [on]);

    return on ? access : CLOSED;
};
